/**
 * Manufacturing Companies API Client
 * Phase 2: Wholesale Partnership Module
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/companies';

// ===== LIST & SEARCH =====

/**
 * Get all manufacturing companies with filters
 */
export const getCompanies = async (params = {}) => {
  try {
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch companies',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get single company by ID
 */
export const getCompanyById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get companies enabled for auto-forwarding
 */
export const getAutoForwardCompanies = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/auto-forward`);
    return response.data;
  } catch (error) {
    console.error('Error fetching auto-forward companies:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch auto-forward companies',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CRUD OPERATIONS =====

/**
 * Create a new manufacturing company
 */
export const createCompany = async (data) => {
  try {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  } catch (error) {
    console.error('Error creating company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update a manufacturing company
 */
export const updateCompany = async (id, data) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Delete a manufacturing company (soft delete)
 */
export const deleteCompany = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== COMPANY MANAGEMENT =====

/**
 * Suspend a company
 */
export const suspendCompany = async (id, reason) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/suspend`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error suspending company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to suspend company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Activate a suspended company
 */
export const activateCompany = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/activate`);
    return response.data;
  } catch (error) {
    console.error('Error activating company:', error);
    throw {
      message: error.response?.data?.error || 'Failed to activate company',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== PERFORMANCE =====

/**
 * Get company performance metrics
 */
export const getCompanyPerformance = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}/performance`);
    return response.data;
  } catch (error) {
    console.error('Error fetching company performance:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch company performance',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update company performance metrics
 */
export const updatePerformanceMetrics = async (id, metrics) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}/performance`, metrics);
    return response.data;
  } catch (error) {
    console.error('Error updating performance metrics:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update performance metrics',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getCompanies,
  getCompanyById,
  getAutoForwardCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  suspendCompany,
  activateCompany,
  getCompanyPerformance,
  updatePerformanceMetrics
};
